/**
 * poseResolver — pure pose-sprite selection logic.
 *
 * No Phaser import — fully testable in node.
 *
 * Selects a texture key per frame based on AnimState and a per-entity pose map.
 * Priority: pose override for this state (if loaded) → base assetId (if loaded) → null.
 */

import type { AnimState } from '@/systems/AnimationStateMachine';
import type { TextureLookup } from '@/systems/entitySpriteResolver';

/**
 * Returns the texture key for an entity's body image given the current anim state.
 *
 * 1. If `poseAssetIds[animState]` exists and is registered in `atlas` → that key.
 * 2. Else if `baseAssetId` is registered in `atlas` → that key.
 * 3. Else → null (caller shows the colour-body fallback).
 */
export function resolvePoseTexture(
  animState: AnimState,
  poseAssetIds: Partial<Record<AnimState, string>> | undefined,
  baseAssetId: string | undefined,
  atlas: TextureLookup,
): string | null {
  const poseId = poseAssetIds?.[animState];
  if (poseId) {
    const tex = atlas.getTexture(poseId);
    if (tex !== null) return tex;
  }
  if (baseAssetId) {
    return atlas.getTexture(baseAssetId);
  }
  return null;
}

/**
 * Returns true when a ko pose sprite is loaded for this entity.
 * When true, Entity.knockOut() should skip the setAngle(90) rotation —
 * the ko pose already depicts the knocked-down posture.
 */
export function hasKoPoseSprite(
  poseAssetIds: Partial<Record<AnimState, string>> | undefined,
  atlas: TextureLookup,
): boolean {
  const poseId = poseAssetIds?.ko;
  if (!poseId) return false;
  return atlas.getTexture(poseId) !== null;
}
